import {
  CatalystInsightCategory,
  CatalystSubcategories,
} from '@api/api/catalyst-insights';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { CatalystInsightIcon } from '../catalyst-insight-icon/CatalystInsightIcon';
import { getCatalystInsightIconProps } from '../catalyst-insight-icon/utils';
import { CatalystInsightTooltip } from '../catalyst-insight-tooltip/CatalystInsightTooltip';
import { useCatalystDrawer } from '../useCatalystDrawer';
import styles from './CatalystInsightPill.module.scss';

const COMPANY_CATALYSTS = 'company';

export interface CatalystInsightPillProps {
  insightId: string;
  entityTag?: string;
  themeId?: string;
  isClickable?: boolean;
  source: PosthogTrackingSource;
  category: CatalystInsightCategory;
  subCategory?: CatalystSubcategories;
}

export const CatalystInsightPill = ({
  insightId,
  entityTag,
  themeId,
  subCategory,
  category,
  isClickable = false,
  source,
}: CatalystInsightPillProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();

  const isCompanyCatalyst = category === COMPANY_CATALYSTS;

  const handleClick = () => {
    if (isClickable) {
      if (isCompanyCatalyst && entityTag) {
        openCompanyCatalystDrawer({
          type: category,
          entityTag,
          insightId,
          source,
        });
      } else if (subCategory) {
        openMarketCatalystDrawer({
          type: subCategory,
          themeId,
          insightId,
          source,
        });
      }
    }
  };

  return (
    <TooltipContainer
      label={
        <CatalystInsightTooltip
          subCategory={subCategory}
          iconProps={getCatalystInsightIconProps({
            isBordered: false,
            isCompanyCatalyst,
          })}
          isCompanyCatalyst={isCompanyCatalyst}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!isClickable}
      inPortal
      withSafePolygon
    >
      <div
        data-testid="catalyst-insight-pill"
        className={clsx(styles.catalystInsightWrapper, {
          [styles.companyCatalyst]: isCompanyCatalyst,
          [styles.marketCatalyst]: !isCompanyCatalyst,
          [styles.clickable]: isClickable,
        })}
        onClick={handleClick}
      >
        <CatalystInsightIcon
          size={12}
          enableHover={false}
          isBordered={false}
          catalystInsightId={insightId}
          category={category}
          subCategory={subCategory}
          source={source}
        />
        <span
          className={clsx(styles.catalystInsightLabel, {
            [styles.companyCatalystLabel]: isCompanyCatalyst,
            [styles.marketCatalystLabel]: !isCompanyCatalyst,
          })}
        >
          {isCompanyCatalyst
            ? t('insights:catalyst.titleCompanyCatalyst')
            : t('insights:catalyst.titleMarketCatalyst')}
        </span>
      </div>
    </TooltipContainer>
  );
};
